import { computed } from 'vue';
import { estimateBatteryHealth } from '@/services/batteryHealth';
import type { BatteryHealthResult } from '@/services/batteryHealth';
import type { ChargingSession } from '@/types';

const DC_POWER_THRESHOLD_KW = 25;

/**
 * Derives DC fast charge fraction from charging session history.
 * Average power = usageBase (kWh) / session duration (hours).
 * Sessions above 25 kW are classified as DC (Supercharger/CCS).
 * Returns null if no usable sessions are found.
 */
function deriveDcFraction(sessions: ChargingSession[]): number | null {
  const usable = sessions.filter(s => {
    const fee = s.fees?.find(f => f.feeType === 'CHARGING');
    if (!fee || typeof fee.usageBase !== 'number' || fee.usageBase <= 0) return false;
    if (!s.chargeStartDateTime || !s.chargeStopDateTime) return false;
    const durationHours = (new Date(s.chargeStopDateTime).getTime() - new Date(s.chargeStartDateTime).getTime()) / 3_600_000;
    return durationHours > 0.05;
  });

  if (usable.length === 0) return null;

  const dcCount = usable.filter(s => {
    const fee = s.fees.find(f => f.feeType === 'CHARGING')!;
    const durationHours = (new Date(s.chargeStopDateTime!).getTime() - new Date(s.chargeStartDateTime!).getTime()) / 3_600_000;
    return fee.usageBase / durationHours > DC_POWER_THRESHOLD_KW;
  }).length;

  return dcCount / usable.length;
}

export interface BatteryProjectionSources {
  dcFractionSource: 'derived' | 'unknown';
  dcFractionSessionCount: number;
}

export interface BatteryProjection {
  result: BatteryHealthResult;
  sources: BatteryProjectionSources;
}

/**
 * Wires report data to the battery health estimation service.
 * All inputs are extracted from real API data , nothing is invented.
 * Limitations (missing data) are surfaced via the sources object.
 */
export function useBatteryProjection(report: () => any): { projection: import('vue').ComputedRef<BatteryProjection | null> } {
  const projection = computed<BatteryProjection | null>(() => {
    const r = report();
    if (!r) return null;

    const measuredSoh = parseFloat(r.batterySummary?.batterySoh ?? '') || 0;
    if (measuredSoh <= 0) return null;

    const vehicleYear = r.vehicle?.year;
    if (!vehicleYear) return null;

    const totalEnergyKwh = r.chargingSummary?.totalEnergyKwh ?? 0;
    const batteryCapacityKwh = parseFloat(String(r.batterySummary?.batteryCapacityKwh ?? '')) || 0;
    const estimatedRangeKm = r.batterySummary?.estimatedRangeKm ?? 0;

    const odometerMiles = r.vehicleData?.vehicle_state?.odometer ?? 0;
    const odometerKm = odometerMiles * 1.60934;

    const chargeLimitSoc =
      r.batterySummary?.chargeLimitSoc ??
      r.vehicleData?.charge_state?.charge_limit_soc ??
      80;

    const region = r.vehicle?.region ?? '';

    const sessions: ChargingSession[] = r.chargingHistory ?? [];
    const dcFraction = deriveDcFraction(sessions);

    const result = estimateBatteryHealth({
      measuredSoh,
      vehicleYear,
      totalEnergyKwh,
      batteryCapacityKwh,
      estimatedRangeKm,
      odometerKm,
      chargeLimitSoc,
      region,
      dcFastChargeFraction: dcFraction,
    });

    return {
      result,
      sources: {
        dcFractionSource: dcFraction !== null ? 'derived' : 'unknown',
        dcFractionSessionCount: sessions.length,
      },
    };
  });

  return { projection };
}
